/**
 * UI compartilhada do Plano de Ação (FACR) — usada IGUAL pelo Portal da Unidade
 * (`portal/PortalUnidade.tsx`) e pela visão interna (`PlanoAvaliador.tsx`, que
 * as telas mobile e desktop embrulham). A UI/UX é a mesma; o que muda é só a
 * CAPACIDADE, controlada por props (`fatoEditavel`, `mostrarCodigo`,
 * `mostrarContextoAvaliacao`) — todas opt-in, nenhuma delas ligada pelo Portal.
 *
 * UNIFICAÇÃO (10/set/2026): o card do avaliador é o card da unidade + uma camada
 * de revisão. Fato, Critério e Causa usam os mesmos blocos; editar achado/causa
 * fica atrás de um link; o rodapé troca o seletor de 3 situações por Aprovar /
 * Rejeitar, que só aparecem com o item em revisão (a regra do servidor).
 *
 * REDESIGN v2 (mais guiado): o achado vira um bloco de contexto ("o problema"),
 * o plano é preenchido em 4 passos numerados com dica/exemplo, o prazo tem chips
 * rápidos (1/2/3 meses, respeitando a trava da CAINT), há pips de progresso por
 * item e trava clara de "Concluído". Toda a lógica de dados, validação e upload
 * de evidências é a MESMA de antes.
 */
import { CATALOGO } from "@caint/data";
import {
  ACCEPT_IMAGEM_PDF,
  CONTENT_TYPES_PLANO_EVIDENCIA,
  LIMITE_BYTES_PLANO_EVIDENCIA,
  MAX_MESES_PRAZO,
  prazoMaximo,
} from "@caint/shared";
import type {
  PlanoAcaoItem,
  PlanoEvidenciaUploadDTO,
  PlanoItem,
  ResponderPlanoItemDTO,
  StatusPlanoItem,
} from "@caint/shared";
// A regra de ordenação vive em `shared`: a MESMA vale aqui, no relatório impresso
// e no gerador do PDF — três implementações seriam três chances de divergir.
import { compararItensDoPlano, gravidadeItem } from "@caint/shared";
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { ApiError, type PlanoEvidenciaRemota, apiClient } from "../../lib/api.ts";
import { prepararEvidencia } from "../../lib/comprimirImagem.ts";
import { NOME_SETOR, SETOR_POR_ITEM, nomeCargo } from "../../lib/planoUnidade.ts";
import { BlocoContextoAvaliacao, contextoAvaliacaoDoItem } from "./contextoAvaliacao.tsx";
import "../../portal/portal.css";

const PERGUNTA_POR_ITEM = new Map(CATALOGO.map((i) => [i.id, i.pergunta]));

const ROTULO_STATUS: Record<StatusPlanoItem, string> = {
  pendente: "Pendente",
  em_andamento: "Em andamento",
  rejeitado: "Rejeitado",
  concluido: "Concluído",
};

/** Chamada da linha recolhida no avaliador: o verbo depende do que falta fazer. */
const CTA_AVALIADOR: Record<StatusPlanoItem, string> = {
  pendente: "Abrir",
  em_andamento: "Revisar",
  rejeitado: "Ver motivo",
  concluido: "Ver",
};

/** `id` do card no DOM — o auto-avanço rola até o próximo item por ele. */
function idDoCard(itemId: string): string {
  return `plano-item-${itemId}`;
}

/** Exemplos curtos e concretos para orientar quem preenche (microcopy). */
const EXEMPLO_CAUSA = "Ex.: a equipe não tinha um protocolo formal para seguir.";
const EXEMPLO_ACAO = "Ex.: elaborar o protocolo, aprovar na direção e treinar a equipe.";
const EXEMPLO_RESP = "Ex.: Enf.ª Maria — coordenação do setor";

/** Data local (YYYY-MM-DD) de uma Date, para os limites do seletor de prazo. */
function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dia}`;
}

/** Limites do seletor "Até quando?": de hoje até hoje+3 meses (trava da CAINT). */
function limitesPrazo(): { min: string; max: string } {
  return { min: isoLocal(new Date()), max: isoLocal(prazoMaximo()) };
}

/** Prazo em N meses a partir de hoje, clampeado à trava (chips rápidos). */
function prazoEmMeses(n: number): string {
  if (n >= MAX_MESES_PRAZO) {
    return isoLocal(prazoMaximo());
  }
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return isoLocal(d);
}

/** Chips de prazo oferecidos (1..MAX meses). */
const CHIPS_PRAZO: readonly { n: number; rotulo: string }[] = [1, 2, 3]
  .filter((n) => n <= MAX_MESES_PRAZO)
  .map((n) => ({ n, rotulo: n === 1 ? "1 mês" : `${n} meses` }));

/** Data legível "30 de setembro de 2026" a partir de YYYY-MM-DD. */
function dataLegivel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** Situação de uma data (YYYY-MM-DD) escolhida frente à trava do prazo. */
function situacaoPrazo(prazo: string): "ok" | "passado" | "alem" {
  const [y, m, d] = prazo.split("-").map(Number);
  const alvo = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  if (alvo < hoje) {
    return "passado";
  }
  const max = prazoMaximo();
  max.setHours(23, 59, 59, 999);
  return alvo > max ? "alem" : "ok";
}

export interface Totais {
  total: number;
  concluidos: number;
  obrig: number;
  obrigOk: number;
  pct: number;
}

/** Consolida os números de progresso de uma lista de itens (fonte da barra). */
export function totaisDeItens(itens: readonly PlanoItem[]): Totais {
  const concluidos = itens.filter((i) => i.status === "concluido").length;
  const obrig = itens.filter((i) => i.obrigatorio);
  const obrigOk = obrig.filter((i) => i.status === "concluido").length;
  const pct = itens.length ? Math.round((concluidos / itens.length) * 100) : 0;
  return { total: itens.length, concluidos, obrig: obrig.length, obrigOk, pct };
}

/* ── Filtros por situação (foco) — telas internas do time CAINT ─────────────── */
export type FiltroPlano = "todos" | "pendente" | "em_andamento" | "rejeitado" | "concluido" | "obrigatorios";

/** `cor` casa com as classes de situação (`.pl-dot`/accent). "A revisar"
 *  (em_andamento) é a FILA de revisão do CAINT: a unidade enviou e aguarda parecer.
 *  `singular` é o selo "1 rejeitado" da lista de setores (o plural é o rótulo). */
export const FILTROS: readonly {
  id: FiltroPlano;
  rotulo: string;
  singular: string;
  cor?: "pendente" | "andamento" | "rejeitado" | "concluido";
}[] = [
  { id: "todos", rotulo: "Todos", singular: "item" },
  { id: "em_andamento", rotulo: "A revisar", singular: "a revisar", cor: "andamento" },
  { id: "rejeitado", rotulo: "Rejeitados", singular: "rejeitado", cor: "rejeitado" },
  { id: "pendente", rotulo: "Pendentes", singular: "pendente", cor: "pendente" },
  { id: "concluido", rotulo: "Concluídos", singular: "concluído", cor: "concluido" },
  { id: "obrigatorios", rotulo: "Obrigatórios", singular: "obrigatório" },
];

/** "3 a revisar", "1 rejeitado": contagem de um filtro, para o selo do setor. */
export function rotuloContagem(f: FiltroPlano, n: number): string {
  const meta = FILTROS.find((x) => x.id === f);
  if (!meta) {
    return String(n);
  }
  return `${n} ${n === 1 ? meta.singular : meta.rotulo.toLowerCase()}`;
}

/** Um item passa no filtro selecionado? */
export function passaFiltro(it: PlanoItem, f: FiltroPlano): boolean {
  if (f === "todos") {
    return true;
  }
  if (f === "obrigatorios") {
    return it.obrigatorio;
  }
  return it.status === f; // pendente | em_andamento | rejeitado | concluido
}

/** Quantos itens caem em um filtro (para o contador do chip). */
export function contarFiltro(itens: readonly PlanoItem[], f: FiltroPlano): number {
  return itens.filter((i) => passaFiltro(i, f)).length;
}

/**
 * O texto que estava aqui foi substituído pelo achado da avaliação?
 *
 * Quem escreveu por último vence (a comparação é feita no servidor, com os
 * carimbos de `evidencia_mi_em` e `fato_em`). Quando a avaliação vence e o texto
 * que sai era escrito por uma pessoa, o servidor guarda o anterior em
 * `fatoSubstituido` — e a UI precisa avisar: substituição silenciosa de conteúdo
 * humano é o tipo de coisa que reaparece como problema numa revisão.
 *
 * O aviso some assim que o campo em edição volta a ter o texto substituído
 * (logo após clicar em "restaurar"), para não ficar pendurado esperando salvar.
 */
export function textoFoiSubstituido(item: Pick<PlanoItem, "fatoSubstituido">, fatoNoCampo: string): boolean {
  const anterior = item.fatoSubstituido;
  return anterior !== null && anterior !== "" && anterior !== fatoNoCampo;
}

/** Setor (05A..05R) de um item — a mesma chave que agrupa, filtra e dá escopo. */
export function setorDoItem(it: Pick<PlanoItem, "itemId">): string {
  return SETOR_POR_ITEM.get(it.itemId) ?? "—";
}

/**
 * Agrupa por setor, ordenando os SETORES pela gravidade somada dos achados
 * (mais crítico primeiro) e os ITENS por severidade dentro de cada um.
 *
 * Antes era alfabético dos dois lados, então um item de maturidade 0 aparecia
 * depois de um de maturidade 2 só porque o código vinha depois — o inverso do
 * útil para quem precisa decidir o que atacar primeiro.
 */
export function agruparPorSetor(
  itens: readonly PlanoItem[],
): { setor: string; nome: string; itens: PlanoItem[] }[] {
  const grupos = new Map<string, PlanoItem[]>();
  for (const it of itens) {
    const setor = setorDoItem(it);
    const lista = grupos.get(setor);
    if (lista) {
      lista.push(it);
    } else {
      grupos.set(setor, [it]);
    }
  }
  return [...grupos.entries()]
    .map(([setor, its]) => ({
      setor,
      nome: NOME_SETOR.get(setor) ?? setor,
      itens: its.slice().sort(compararItensDoPlano),
      gravidade: its.reduce((soma, it) => soma + gravidadeItem(it), 0),
    }))
    .sort((a, b) => b.gravidade - a.gravidade || a.setor.localeCompare(b.setor))
    .map(({ setor, nome, itens: its }) => ({ setor, nome, itens: its }));
}

/* ---------------- Ações do item (FACR: A e R) ---------------- */

/** Ação em edição: o prazo fica em `yyyy-mm-dd`, que é o que o <input date> usa. */
export interface AcaoEdicao {
  texto: string;
  responsavel: string;
  prazo: string;
}

export { compararItensDoPlano, gravidadeItem };

const acaoVazia = (): AcaoEdicao => ({ texto: "", responsavel: "", prazo: "" });

function paraEdicao(a: PlanoAcaoItem): AcaoEdicao {
  return { texto: a.texto, responsavel: a.responsavel, prazo: a.prazo ? a.prazo.slice(0, 10) : "" };
}

/**
 * Ações que valem gravar: as que têm ao menos texto. Linha totalmente em branco
 * é ruído de digitação, não intenção — some no salvamento.
 */
export function acoesPreenchidas(acoes: readonly AcaoEdicao[]): AcaoEdicao[] {
  return acoes.filter((a) => a.texto.trim() !== "");
}

/**
 * O que falta para o item poder ser dado como concluído.
 *
 * Cada ação precisa do trio completo (o quê / quem / quando): sem isso, uma
 * linha pela metade deixaria "concluído" significar coisas diferentes conforme
 * o item. Com várias ações, a exigência vale para TODAS — é o que torna o plano
 * cobrável de fato.
 */
export function faltaNasAcoes(acoes: readonly AcaoEdicao[]): string[] {
  const uteis = acoesPreenchidas(acoes);
  if (uteis.length === 0) {
    return ["o que vão fazer"];
  }
  const faltas: string[] = [];
  if (uteis.some((a) => !a.responsavel.trim())) {
    faltas.push("quem vai fazer (em todas as ações)");
  }
  if (uteis.some((a) => !a.prazo)) {
    faltas.push("até quando (em todas as ações)");
  }
  return faltas;
}

/**
 * Lista de ações do item. Cada linha carrega o próprio dono e prazo — decisão de
 * 24/ago/2026, para a cobrança ser por ação e não por item.
 *
 * Ao adicionar uma linha nova, o responsável da anterior vem preenchido: no caso
 * comum é a mesma pessoa, e economizar isso importa num plano de ~190 itens.
 */
function ListaAcoes({
  acoes,
  onChange,
  numero,
  idBase,
  rotulo,
}: {
  acoes: AcaoEdicao[];
  onChange: (a: AcaoEdicao[]) => void;
  numero: number;
  idBase: string;
  rotulo: string;
}) {
  const lim = limitesPrazo();
  const alterar = (i: number, patch: Partial<AcaoEdicao>) =>
    onChange(acoes.map((a, k) => (k === i ? { ...a, ...patch } : a)));
  const adicionar = () =>
    onChange([...acoes, { ...acaoVazia(), responsavel: acoes[acoes.length - 1]?.responsavel ?? "" }]);
  const remover = (i: number) =>
    onChange(acoes.length === 1 ? [acaoVazia()] : acoes.filter((_, k) => k !== i));

  return (
    <div className="pl-step">
      <span className="pl-step-lbl">
        <span className="pl-num">{numero}</span> {rotulo}
      </span>
      <p className="pl-step-hint">
        Uma linha por ação. Cada uma tem seu responsável e seu prazo — assim dá para cobrar separadamente.
      </p>

      {acoes.map((a, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a linha É a posição; não há id estável
        <div className="pl-acao" key={i}>
          <div className="pl-acao-hd">
            <span className="pl-acao-n">{i + 1}</span>
            {acoes.length > 1 && (
              <button
                type="button"
                className="pl-acao-rm"
                onClick={() => remover(i)}
                aria-label={`Remover ação ${i + 1}`}
              >
                ✕
              </button>
            )}
          </div>
          <textarea
            id={`acao-${idBase}-${i}`}
            className="pl-textarea"
            rows={2}
            value={a.texto}
            placeholder={EXEMPLO_ACAO}
            onChange={(e) => alterar(i, { texto: e.target.value })}
          />
          <div className="pl-acao-duo">
            <div>
              <span className="pl-acao-lbl">Quem vai fazer</span>
              <input
                className="pl-input"
                value={a.responsavel}
                placeholder={EXEMPLO_RESP}
                onChange={(e) => alterar(i, { responsavel: e.target.value })}
              />
            </div>
            <div>
              <span className="pl-acao-lbl">Até quando</span>
              <div className="pl-chips">
                {CHIPS_PRAZO.map((c) => {
                  const iso = prazoEmMeses(c.n);
                  return (
                    <button
                      key={c.n}
                      type="button"
                      className={`pl-chip${a.prazo === iso ? " on" : ""}`}
                      onClick={() => alterar(i, { prazo: iso })}
                    >
                      {c.rotulo}
                    </button>
                  );
                })}
                <label className="pl-chip pl-chip-date">
                  <input
                    type="date"
                    value={a.prazo}
                    min={lim.min}
                    max={lim.max}
                    onChange={(e) => alterar(i, { prazo: e.target.value })}
                  />
                </label>
              </div>
              {a.prazo && <div className="pl-prazo-val">Prazo: {dataLegivel(a.prazo)}</div>}
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="pl-acao-add" onClick={adicionar}>
        + adicionar ação
      </button>
    </div>
  );
}

/** Campos que a metodologia exige para dar um item como "concluído". */
function faltaParaConcluir(acoes: readonly AcaoEdicao[], temEvidencia: boolean): string[] {
  const faltando = [...faltaNasAcoes(acoes)];
  if (!temEvidencia) {
    faltando.push("anexar uma evidência");
  }
  return faltando;
}

const MB_LIMITE_EVIDENCIA = Math.round(LIMITE_BYTES_PLANO_EVIDENCIA / 1_000_000);

/** Rótulo curto de uma evidência (tipo + tamanho legível). */
function rotuloEvidencia(ev: PlanoEvidenciaRemota): string {
  const tipo = ev.contentType === "application/pdf" ? "Documento PDF" : "Imagem";
  const mb = ev.tamanhoBytes / 1_000_000;
  const tam = mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(ev.tamanhoBytes / 1000))} KB`;
  return `${tipo} · ${tam}`;
}

/** Atribuição de uma evidência: "Ana Sousa (Coordenador) · 22/07 14:30". */
function autoriaEvidencia(ev: PlanoEvidenciaRemota): string {
  const quando = new Date(ev.criadoEm).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!ev.criadoPorNome) {
    return quando;
  }
  const cargo = ev.criadoPorCargo ? nomeCargo(ev.criadoPorCargo) : "";
  return cargo ? `${ev.criadoPorNome} (${cargo}) · ${quando}` : `${ev.criadoPorNome} · ${quando}`;
}

/** Ícone de check (SVG inline, sem dependência). */
function IconeCheck() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <title>concluído</title>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Barra de progresso do plano (efeito de meta). A mesma no Portal e na visão do
 * avaliador — que acrescenta o escopo (`topo`) e os KPIs por situação (`children`)
 * dentro do mesmo cartão.
 */
export function BarraProgresso({
  totais,
  topo,
  children,
}: {
  totais: Totais;
  topo?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="pl-progresso">
      {topo}
      <div className="pl-progresso-topo">
        <span>
          <strong>{totais.concluidos}</strong> de <strong>{totais.total}</strong> itens concluídos
        </span>
        <span className="pl-progresso-pct">{totais.pct}%</span>
      </div>
      {/* Barra decorativa: o valor já é anunciado pelo texto acima (X de Y · Z%). */}
      <div className="pl-barra" aria-hidden="true">
        <div className="pl-barra-fill" style={{ width: `${totais.pct}%` }} />
      </div>
      {totais.obrig > 0 && (
        <div className="pl-progresso-obrig">
          Itens obrigatórios: {totais.obrigOk} de {totais.obrig}
        </div>
      )}
      {children}
    </div>
  );
}

/** Texto do rodapé de revisão (avaliador), por situação do item. */
function textoRevisao(status: StatusPlanoItem, nEvidencias: number): string {
  switch (status) {
    case "em_andamento":
      return nEvidencias > 0
        ? `A unidade enviou ${nEvidencias} ${nEvidencias === 1 ? "comprovação" : "comprovações"}. Confira e decida.`
        : "A unidade enviou para revisão. Confira e decida.";
    case "rejeitado":
      return "Aguardando a unidade corrigir e reenviar.";
    case "concluido":
      return "Aprovado pelo time CAINT.";
    default:
      return "Aguardando a unidade preencher o plano e anexar a comprovação.";
  }
}

export interface ItemPlanoCardProps {
  item: PlanoItem;
  token: string;
  onSalvo: (i: PlanoItem) => void;
  /** Interno pode editar o Fato (achado); unidade só lê. */
  fatoEditavel?: boolean;
  /** Interno vê o código do item e a marca "Manual". */
  mostrarCodigo?: boolean;
  /**
   * Interno vê o contexto da avaliação (nota de maturidade + evidência
   * sugerida), só leitura. Prop própria — e não um `fatoEditavel` reaproveitado
   * — porque ver o critério e poder editar o achado são capacidades diferentes:
   * uma futura tela interna de leitura precisa de uma sem a outra.
   */
  mostrarContextoAvaliacao?: boolean;
  /** Abre o card já expandido (usado para o primeiro pendente da lista). */
  abrirInicial?: boolean;
  /** Aberto controlado (opcional): quando passado, o PAI controla a abertura
   *  (usado no Portal p/ manter só um item aberto e auto-avançar). */
  aberto?: boolean;
  onAberto?: (aberto: boolean) => void;
  /** Após salvar (unidade) ou decidir a revisão (avaliador): pede ao pai o próximo item. */
  onAvancar?: () => void;
  /** Aviso curto ("Plano salvo ✓") mostrado pela casca (toast do Portal). */
  onNotificar?: (msg: string) => void;
}

/** Um item-ação FACR: mesma UI nas telas; capacidade varia pelas props. */
export function ItemPlanoCard({
  item,
  token,
  onSalvo,
  fatoEditavel,
  mostrarCodigo,
  mostrarContextoAvaliacao,
  abrirInicial,
  aberto: abertoProp,
  onAberto,
  onAvancar,
  onNotificar,
}: ItemPlanoCardProps) {
  const [fato, setFato] = useState(item.fato ?? "");
  // Sempre ao menos UMA linha: campo vazio convida a preencher; lista vazia não.
  const [acoes, setAcoes] = useState<AcaoEdicao[]>(() =>
    item.acoes.length > 0 ? item.acoes.map(paraEdicao) : [acaoVazia()],
  );
  const [causa, setCausa] = useState(item.causa ?? "");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Motivo digitado pelo time CAINT ao rejeitar a evidência (só telas internas).
  const [motivoRej, setMotivoRej] = useState("");
  const [rejeitando, setRejeitando] = useState(false);
  // Achado e causa são LEITURA por padrão também no avaliador; editar é um clique.
  const [editandoFato, setEditandoFato] = useState(false);
  const [editandoCausa, setEditandoCausa] = useState(false);
  // Item sem plano da unidade: o avaliador vê o aviso; preencher por ela é opt-in.
  const [preencherPelaUnidade, setPreencherPelaUnidade] = useState(false);
  const foiSubstituido = textoFoiSubstituido(item, fato);
  const [salvo, setSalvo] = useState(false);
  // Abertura: controlada pelo pai quando `abertoProp` vem (Portal), senão interna.
  const [abertoLocal, setAbertoLocal] = useState(abrirInicial ?? false);
  const controlado = abertoProp !== undefined;
  const aberto = controlado ? abertoProp : abertoLocal;
  const definirAberto = (v: boolean) => {
    if (controlado) {
      onAberto?.(v);
    } else {
      setAbertoLocal(v);
    }
  };
  // Evidências do item (comprovação). Carregadas quando o card está aberto.
  const [evidencias, setEvidencias] = useState<PlanoEvidenciaRemota[]>([]);
  // B2 do plano não configurado (503): relaxa a trava (não há como anexar).
  const [evidIndisponivel, setEvidIndisponivel] = useState(false);
  const [enviandoEvid, setEnviandoEvid] = useState(false);
  const [erroEvid, setErroEvid] = useState<string | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!aberto) {
      return;
    }
    let vivo = true;
    apiClient
      .listarPlanoEvidencias(item.id, token)
      .then((evs) => {
        if (vivo) {
          setEvidencias(evs);
        }
      })
      .catch((e) => {
        if (vivo && e instanceof ApiError && e.status === 503) {
          setEvidIndisponivel(true);
        }
      });
    return () => {
      vivo = false;
    };
  }, [aberto, item.id, token]);

  const pergunta = PERGUNTA_POR_ITEM.get(item.itemId) ?? item.itemId;
  // A UNIDADE preenche o PLANO: o que fará (ação), quem (responsável) e até quando
  // (prazo). Fato e Causa são o diagnóstico do time CAINT — a unidade não edita, e
  // quem audita/conclui/rejeita é o CAINT. `fatoEditavel` marca as telas internas.
  const ehUnidade = !fatoEditavel;
  // A unidade recebe SEMPRE (é o Fato dela); o interno, sob a prop de sempre.
  const ctxAvaliacao = contextoAvaliacaoDoItem(item, mostrarContextoAvaliacao || ehUnidade);
  const acoesSalvas = JSON.stringify(item.acoes.length > 0 ? item.acoes.map(paraEdicao) : [acaoVazia()]);
  const acoesMudaram = JSON.stringify(acoes) !== acoesSalvas;
  const alterado =
    (fatoEditavel && fato !== (item.fato ?? "")) || acoesMudaram || causa !== (item.causa ?? "");
  // Sem storage do plano configurado, a evidência não é exigível.
  const temEvidencia = evidIndisponivel || evidencias.length > 0;
  const faltando = faltaParaConcluir(acoes, temEvidencia);
  const podeConcluir = faltando.length === 0;
  // Pips: a lista de ações conta como UM campo — completa quando todas as ações
  // têm o trio (o quê/quem/quando). O interno acompanha também a Causa.
  const acoesOk = acoesPreenchidas(acoes).length > 0 && faltaNasAcoes(acoes).length === 0;
  const camposPlano = ehUnidade ? [acoesOk] : [causa.trim() !== "", acoesOk];
  const nFill = camposPlano.filter(Boolean).length;
  const nPips = camposPlano.length;
  // O avaliador lê o plano que a UNIDADE escreveu; sem nenhuma ação salva, avisa
  // em vez de abrir um formulário vazio (preencher por ela continua possível).
  const semPlanoDaUnidade = !ehUnidade && item.acoes.length === 0 && !preencherPelaUnidade;

  const enviarEvidencia = async (e: ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ""; // permite reescolher o mesmo arquivo depois de um erro
    if (!arquivo) {
      return;
    }
    if (!(CONTENT_TYPES_PLANO_EVIDENCIA as readonly string[]).includes(arquivo.type)) {
      setErroEvid("Formato não aceito. Envie uma imagem (JPG, PNG, WEBP) ou um PDF.");
      return;
    }
    setEnviandoEvid(true);
    setErroEvid(null);
    try {
      // Comprime antes de medir e de pedir o presign: o servidor reconfere o
      // tamanho real por HEAD, então tudo (limite, presign, confirmar) precisa
      // falar do arquivo que vai REALMENTE subir, não do que veio da câmera.
      const { arquivo: envio, miniatura } = await prepararEvidencia(arquivo);
      if (envio.size > LIMITE_BYTES_PLANO_EVIDENCIA) {
        setErroEvid(`Arquivo muito grande. O limite é ${MB_LIMITE_EVIDENCIA} MB por arquivo.`);
        return;
      }
      const contentType = envio.type;
      const tipo = contentType as PlanoEvidenciaUploadDTO["contentType"];
      const preparado = await apiClient.presignPlanoEvidencia(
        { planoItemId: item.id, contentType: tipo, tamanhoBytes: envio.size },
        token,
      );
      await apiClient.enviarArquivo(preparado.uploadUrl, envio, contentType);
      // Miniatura é acessório: se falhar, a evidência já subiu e o registro segue
      // sem ela (a galeria cai na imagem cheia).
      let chaveMiniatura: string | undefined;
      if (miniatura) {
        try {
          await apiClient.enviarArquivo(preparado.uploadUrlMiniatura, miniatura, miniatura.type);
          chaveMiniatura = preparado.chaveMiniatura;
        } catch {
          chaveMiniatura = undefined;
        }
      }
      await apiClient.confirmarPlanoEvidencia(
        {
          planoItemId: item.id,
          contentType: tipo,
          tamanhoBytes: envio.size,
          id: preparado.id,
          chaveB2: preparado.chaveB2,
          chaveMiniatura,
        },
        token,
      );
      setEvidencias(await apiClient.listarPlanoEvidencias(item.id, token));
    } catch (err) {
      setErroEvid(err instanceof ApiError ? err.message : "Falha ao enviar a evidência.");
    } finally {
      setEnviandoEvid(false);
    }
  };

  const excluirEvidencia = async (id: string) => {
    setErroEvid(null);
    try {
      await apiClient.removerPlanoEvidencia(id, item.id, token);
      setEvidencias((evs) => evs.filter((ev) => ev.id !== id));
    } catch (err) {
      setErroEvid(err instanceof ApiError ? err.message : "Falha ao excluir a evidência.");
    }
  };

  // Rejeição da evidência pelo time CAINT (só telas internas): volta o item para
  // "rejeitado" com o motivo; a unidade corrige e reenvia (→ em andamento).
  const rejeitar = async () => {
    if (!motivoRej.trim()) {
      setErro("Informe o motivo da rejeição.");
      return;
    }
    const ok = await enviar(
      { status: "rejeitado", motivoRejeicao: motivoRej.trim() },
      "Devolvido à unidade com o motivo",
      "Falha ao rejeitar",
    );
    if (ok) {
      setMotivoRej("");
      setRejeitando(false);
      onAvancar?.();
    }
  };

  /** Grava no servidor, repassa o item ao pai e avisa. `true` se deu certo. */
  const enviar = async (dto: ResponderPlanoItemDTO, aviso: string, falha: string): Promise<boolean> => {
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      const atualizado = await apiClient.responderPlanoItem(item.id, dto, token);
      onSalvo(atualizado);
      onNotificar?.(aviso);
      return true;
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : falha);
      return false;
    } finally {
      setSalvando(false);
    }
  };

  /**
   * O que foi editado neste card, pronto para o DTO — ou `null` se a trava do
   * prazo recusou. As ações só vão quando MUDARAM: reenviá-las intactas faria a
   * trava do prazo barrar o avaliador de aprovar (ou só ajustar a causa) de um
   * item cujo prazo já passou — o que é o caso normal de um item já executado.
   */
  const edicoes = (): ResponderPlanoItemDTO | null => {
    const dto: ResponderPlanoItemDTO = {};
    if (acoesMudaram) {
      // Trava do prazo, por AÇÃO: no passado não; no máximo 3 meses.
      for (const [i, a] of acoesPreenchidas(acoes).entries()) {
        const sit = a.prazo ? situacaoPrazo(a.prazo) : null;
        if (sit === "passado") {
          setErro(`Ação ${i + 1}: o prazo não pode ser no passado.`);
          return null;
        }
        if (sit === "alem") {
          setErro(`Ação ${i + 1}: o prazo máximo é de ${MAX_MESES_PRAZO} meses.`);
          return null;
        }
      }
      // As AÇÕES são a fonte única: `responsavel` e `prazo` do item são derivados
      // no servidor a partir delas, e por isso não vão no payload.
      dto.acoes = acoesPreenchidas(acoes).map((a) => ({
        texto: a.texto.trim(),
        responsavel: a.responsavel.trim(),
        prazo: a.prazo ? new Date(`${a.prazo}T12:00:00`).toISOString() : null,
      }));
    }
    // Fato e Causa são do CAINT: a unidade nunca os envia (o servidor ignoraria).
    if (!ehUnidade && causa !== (item.causa ?? "")) {
      dto.causa = causa.trim() || null;
    }
    if (fatoEditavel && fato !== (item.fato ?? "")) {
      dto.fato = fato.trim() || null;
    }
    return dto;
  };

  const salvar = async () => {
    const dto = edicoes();
    if (!dto) {
      return;
    }
    const ok = await enviar(dto, ehUnidade ? "Plano salvo ✓" : "Alterações salvas ✓", "Falha ao salvar");
    if (ok) {
      setSalvo(true);
      setEditandoFato(false);
      setEditandoCausa(false);
      // Unidade: avança para o próximo item. O avaliador só avança ao DECIDIR.
      if (ehUnidade) {
        onAvancar?.();
      }
    }
  };

  // Aprovação (avaliador): conclui levando junto o que foi editado no card.
  const aprovar = async () => {
    if (!podeConcluir) {
      setErro(`Para aprovar, ainda falta: ${faltando.join(", ")}.`);
      return;
    }
    const dto = edicoes();
    if (!dto) {
      return;
    }
    const ok = await enviar({ ...dto, status: "concluido" }, "Item concluído ✓", "Falha ao aprovar");
    if (ok) {
      onAvancar?.();
    }
  };

  // Reabrir um concluído volta para a revisão (transição livre no servidor).
  const reabrir = () =>
    void enviar({ status: "em_andamento" }, "Item reaberto para revisão", "Falha ao reabrir");

  const concluido = item.status === "concluido";

  // Resumo recolhido: check + pergunta + tags + CTA. Toque abre.
  if (!aberto) {
    const parcial =
      item.status !== "concluido" && (item.acao || item.causa || item.responsavel || item.prazo);
    // O avaliador lê a fila pela situação ("Revisar" é o chamado para agir).
    const cta = ehUnidade
      ? item.status === "concluido"
        ? "Ver"
        : parcial
          ? "Continuar"
          : "Preencher"
      : CTA_AVALIADOR[item.status];
    return (
      <div id={idDoCard(item.id)} className={`pl-card${concluido ? " concluido" : ""}`}>
        <button type="button" className="pl-row" onClick={() => definirAberto(true)}>
          <span className={`pl-check ${item.status === "concluido" ? "done" : parcial ? "prog" : ""}`}>
            {item.status === "concluido" ? (
              <IconeCheck />
            ) : parcial ? (
              <span className="pl-check-dot" />
            ) : null}
          </span>
          <span className="pl-row-main">
            <span className="pl-row-q">{pergunta}</span>
            <span className="pl-row-meta">
              {mostrarCodigo && <span className="pl-item-cod">{item.itemId}</span>}
              <span className={`pl-tag ${item.obrigatorio ? "pl-tag-core" : "pl-tag-sug"}`}>
                {item.obrigatorio ? "Obrigatório" : "Sugerido"}
              </span>
              <span className={`pl-tag pl-st-${item.status}`}>{ROTULO_STATUS[item.status]}</span>
            </span>
          </span>
          <span className={`pl-row-cta${!ehUnidade && item.status === "em_andamento" ? " rev" : ""}`}>
            {cta} ›
          </span>
        </button>
      </div>
    );
  }

  return (
    <div id={idDoCard(item.id)} className={`pl-card aberto${concluido ? " concluido" : ""}`}>
      <div className="pl-card-head">
        <span className={item.obrigatorio ? "pl-tag pl-tag-core" : "pl-tag pl-tag-sug"}>
          {item.obrigatorio ? "Obrigatório" : "Sugerido"}
        </span>
        {mostrarCodigo && <span className="pl-item-cod">{item.itemId}</span>}
        {mostrarCodigo && item.origem === "manual" && <span className="pl-tag">Manual</span>}
        {item.gatilhoResolvido && <span className="pl-tag pl-tag-ok">Já resolvido</span>}
        {alterado && <span className="pl-tag pl-tag-dirty">Não salvo</span>}
        <button
          type="button"
          className="pl-recolher"
          onClick={() => definirAberto(false)}
          aria-label="Recolher item"
        >
          Recolher ⌄
        </button>
      </div>

      {/* FATO: o mesmo bloco nas duas visões (nota + rubrica). No portal da
          UNIDADE o texto do achado não é exibido (pedido de 21/ago/2026); no
          avaliador ele aparece em leitura, e editar é um clique. */}
      {ehUnidade ? (
        ctxAvaliacao && <BlocoContextoAvaliacao ctx={ctxAvaliacao} comoFato />
      ) : (
        <BlocoContextoAvaliacao
          ctx={ctxAvaliacao}
          comoFato
          mostrarEvidencia={mostrarContextoAvaliacao}
          acao={
            <button type="button" className="pl-link" onClick={() => setEditandoFato((v) => !v)}>
              {editandoFato ? "Fechar edição" : fato.trim() ? "Editar achado" : "Registrar achado"}
            </button>
          }
        >
          {editandoFato ? (
            <>
              <label className="pl-mini-lbl" htmlFor={`fato-${item.id}`}>
                Texto do achado (a unidade não vê)
              </label>
              <textarea
                id={`fato-${item.id}`}
                className="pl-textarea"
                rows={2}
                value={fato}
                placeholder="Constatação da avaliação"
                onChange={(e) => setFato(e.target.value)}
              />
            </>
          ) : fato.trim() ? (
            <p className="pl-fato-txt">“{fato}”</p>
          ) : null}
          {/* A avaliação foi corrigida DEPOIS deste texto ter sido escrito, então
              o achado novo prevaleceu. Quem escreveu precisa saber — e poder
              voltar atrás, caso a redação anterior fosse melhor. Fica visível
              mesmo com a edição fechada: substituição silenciosa não pode. */}
          {foiSubstituido && (
            <div className="pl-achado pl-achado-diverge">
              <span className="pl-achado-label">A avaliação substituiu o texto anterior</span>
              <p>{item.fatoSubstituido}</p>
              <button
                type="button"
                className="pl-btn-secundario"
                onClick={() => {
                  setFato(item.fatoSubstituido ?? "");
                  setEditandoFato(true);
                }}
              >
                Restaurar o texto anterior
              </button>
            </div>
          )}
        </BlocoContextoAvaliacao>
      )}

      {/* A pergunta desce: vira contexto do critério, não a manchete do card.
          Sozinha ela fazia o plano parecer questionário. */}
      <p className="pl-criterio">
        <span>Critério avaliado</span> {pergunta}
      </p>

      {/* CAUSA: diagnóstico do CAINT. A unidade só lê; o avaliador lê o mesmo
          bloco e edita por um link. */}
      {ehUnidade ? (
        item.causa && (
          <div className="pl-achado pl-achado-causa">
            <span className="pl-achado-label">Causa apontada pelo CAINT</span>
            <p>{item.causa}</p>
          </div>
        )
      ) : editandoCausa ? (
        <div className="pl-achado pl-achado-causa">
          <div className="pl-achado-hd">
            <label className="pl-achado-label" htmlFor={`causa-${item.id}`}>
              Causa apontada pelo CAINT
            </label>
            <button type="button" className="pl-link" onClick={() => setEditandoCausa(false)}>
              Fechar edição
            </button>
          </div>
          <p className="pl-ajuda-campo">A causa principal — o motivo de fundo do problema.</p>
          <textarea
            id={`causa-${item.id}`}
            className="pl-textarea"
            rows={2}
            value={causa}
            placeholder={EXEMPLO_CAUSA}
            onChange={(e) => setCausa(e.target.value)}
          />
        </div>
      ) : causa.trim() ? (
        <div className="pl-achado pl-achado-causa">
          <div className="pl-achado-hd">
            <span className="pl-achado-label">Causa apontada pelo CAINT</span>
            <button type="button" className="pl-link" onClick={() => setEditandoCausa(true)}>
              Editar
            </button>
          </div>
          <p>{causa}</p>
        </div>
      ) : (
        <button type="button" className="pl-add-causa" onClick={() => setEditandoCausa(true)}>
          + Registrar a causa
        </button>
      )}

      <div className="pl-plano-hd">
        <h4>{ehUnidade ? "Seu plano" : "Plano da unidade"}</h4>
        <span className="pl-fill">
          <span className="pl-pips">
            {[0, 1, 2, 3].slice(0, nPips).map((i) => (
              <span key={i} className={`pl-pip${i < nFill ? " on" : ""}`} />
            ))}
          </span>
          {nFill} de {nPips}
        </span>
      </div>

      {semPlanoDaUnidade ? (
        <p className="pl-vazio-plano">
          A unidade ainda não preencheu o plano deste item.{" "}
          <button type="button" className="pl-link" onClick={() => setPreencherPelaUnidade(true)}>
            Preencher mesmo assim
          </button>
        </p>
      ) : (
        <ListaAcoes
          acoes={acoes}
          onChange={setAcoes}
          numero={1}
          idBase={item.id}
          rotulo={ehUnidade ? "O que vocês vão fazer?" : "O que a unidade vai fazer?"}
        />
      )}

      {!evidIndisponivel && (
        <div className="pl-evid">
          <div className="pl-evid-hd">
            <span className="pl-evid-t">
              Comprovação
              <small>
                Uma foto ou PDF que prove a execução — obrigatória para concluir. Máx. {MB_LIMITE_EVIDENCIA}{" "}
                MB.
              </small>
            </span>
            <button
              type="button"
              className="pl-evid-add"
              disabled={enviandoEvid}
              onClick={() => arquivoRef.current?.click()}
            >
              {enviandoEvid ? "Enviando…" : "Anexar"}
            </button>
          </div>
          {evidencias.length > 0 && (
            <ul className="pl-evid-lista">
              {evidencias.map((ev) => (
                <li key={ev.id} className="pl-evid-item">
                  <span className="pl-evid-info">
                    <a className="pl-evid-link" href={ev.url} target="_blank" rel="noreferrer">
                      {rotuloEvidencia(ev)}
                    </a>
                    <span className="pl-evid-autor">{autoriaEvidencia(ev)}</span>
                  </span>
                  <button
                    type="button"
                    className="pl-evid-excluir"
                    onClick={() => void excluirEvidencia(ev.id)}
                  >
                    Excluir
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* `accept` explícito (não `image/*`) faz o iOS converter HEIC→JPEG sozinho. */}
          <input
            ref={arquivoRef}
            type="file"
            accept={ACCEPT_IMAGEM_PDF}
            hidden
            onChange={(e) => void enviarEvidencia(e)}
          />
          {erroEvid && (
            <p role="alert" className="pl-erro">
              {erroEvid}
            </p>
          )}
        </div>
      )}

      {ehUnidade ? (
        <>
          <div className="pl-unidade-rev">
            <span className={`pl-tag pl-st-${item.status}`}>{ROTULO_STATUS[item.status]}</span>
            {item.status === "rejeitado" && item.motivoRejeicao ? (
              <p className="pl-hint pl-hint-rej">
                Rejeitado pelo CAINT: {item.motivoRejeicao}. Ajuste o plano ou a evidência e salve.
              </p>
            ) : (
              <p className="pl-step-hint">
                {item.status === "concluido"
                  ? "Item concluído pelo time CAINT."
                  : item.status === "em_andamento"
                    ? "Enviado — o time CAINT vai revisar. Você ainda pode ajustar o plano."
                    : "Preencha o plano, anexe a comprovação e salve. O time CAINT revisa e conclui."}
              </p>
            )}
          </div>
          {item.status !== "concluido" && faltando.length > 0 && (
            <p className="pl-hint">Ainda falta: {faltando.join(", ")}.</p>
          )}
          <div className="pl-foot">
            <span className="pl-step-hint" style={{ flex: 1 }}>
              Cada item é salvo separadamente — você pode voltar depois.
            </span>
            <button
              type="button"
              className={`m-btn primary pl-salvar${!alterado && salvo ? " ok" : ""}`}
              disabled={salvando || !alterado}
              onClick={() => void salvar()}
            >
              {salvando ? "Salvando…" : !alterado && salvo ? "Salvo ✓" : "Salvar"}
            </button>
          </div>
        </>
      ) : (
        // Rodapé de REVISÃO. Sem seletor de situação: a unidade move o item para
        // "em andamento" ao anexar a comprovação, e o servidor só aceita concluir
        // ou rejeitar a partir daí — então Aprovar/Rejeitar só aparecem nesse caso.
        <div className="pl-revisao">
          <div className="pl-revisao-top">
            <span className={`pl-tag pl-st-${item.status}`}>{ROTULO_STATUS[item.status]}</span>
            <span className="pl-revisao-txt">
              {rejeitando ? (
                <>
                  <b>Rejeitar a comprovação.</b> O item volta para a unidade com o motivo abaixo.
                </>
              ) : (
                textoRevisao(item.status, evidencias.length)
              )}
            </span>
            {concluido && (
              <button type="button" className="pl-link" disabled={salvando} onClick={reabrir}>
                Reabrir
              </button>
            )}
          </div>
          {item.status === "rejeitado" && item.motivoRejeicao && (
            <p className="pl-hint pl-hint-rej">Motivo enviado: {item.motivoRejeicao}.</p>
          )}
          {item.status === "em_andamento" && rejeitando && (
            <>
              <label className="pl-mini-lbl" htmlFor={`motivo-${item.id}`}>
                Motivo (a unidade verá e corrigirá)
              </label>
              <textarea
                id={`motivo-${item.id}`}
                className="pl-textarea"
                rows={2}
                value={motivoRej}
                placeholder="Ex.: a foto não mostra o checklist preenchido; envie o de uma inserção real."
                onChange={(e) => setMotivoRej(e.target.value)}
              />
            </>
          )}
          {item.status === "em_andamento" && !rejeitando && !podeConcluir && (
            <p className="pl-hint">Para aprovar, ainda falta: {faltando.join(", ")}.</p>
          )}
          {(alterado || item.status === "em_andamento") && (
            <div className="pl-revisao-acoes">
              {alterado && !rejeitando && (
                <button
                  type="button"
                  className="pl-btn pl-btn-sec"
                  disabled={salvando}
                  onClick={() => void salvar()}
                >
                  {salvando ? "Salvando…" : "Salvar alterações"}
                </button>
              )}
              {item.status === "em_andamento" &&
                (rejeitando ? (
                  <>
                    <button
                      type="button"
                      className="pl-btn pl-btn-sec"
                      disabled={salvando}
                      onClick={() => {
                        setRejeitando(false);
                        setErro(null);
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="pl-btn pl-btn-perigo"
                      disabled={salvando}
                      onClick={() => void rejeitar()}
                    >
                      Rejeitar e devolver
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="pl-btn pl-btn-rejeitar"
                      disabled={salvando}
                      onClick={() => setRejeitando(true)}
                    >
                      Rejeitar…
                    </button>
                    <button
                      type="button"
                      className="pl-btn pl-btn-aprovar"
                      disabled={salvando || !podeConcluir}
                      onClick={() => void aprovar()}
                    >
                      Aprovar e concluir
                    </button>
                  </>
                ))}
            </div>
          )}
        </div>
      )}
      {erro && (
        <p role="alert" className="pl-erro">
          {erro}
        </p>
      )}
    </div>
  );
}

type Grupo = { setor: string; nome: string; itens: PlanoItem[] };

/**
 * Abertura inicial dos cards. Unidade: o 1º pendente de CADA setor (ela preenche
 * setor a setor). Avaliador: um só item no plano — o 1º a revisar, senão o 1º
 * pendente —, porque ele percorre uma fila, não um formulário.
 */
export function aberturaInicial(grupos: readonly Grupo[], ehUnidade: boolean): Record<string, string> {
  if (ehUnidade) {
    const abertos: Record<string, string> = {};
    for (const g of grupos) {
      const pendente = g.itens.find((i) => i.status !== "concluido");
      if (pendente) {
        abertos[g.setor] = pendente.id;
      }
    }
    return abertos;
  }
  const todos = grupos.flatMap((g) => g.itens.map((i) => ({ setor: g.setor, i })));
  const alvo =
    todos.find((x) => x.i.status === "em_andamento") ?? todos.find((x) => x.i.status !== "concluido");
  return alvo ? { [alvo.setor]: alvo.i.id } : {};
}

/**
 * Próximo item depois de salvar (unidade) ou decidir a revisão (avaliador).
 * Unidade: o próximo não concluído do MESMO setor. Avaliador: o próximo "a
 * revisar" do escopo inteiro, dando a volta — aprovar vira um fluxo contínuo.
 *
 * Recebe os grupos de ANTES do salvamento (o item atual ainda com a situação
 * velha); por isso o atual é excluído pela posição, não pela situação.
 */
export function proximoItem(
  grupos: readonly Grupo[],
  idAtual: string,
  ehUnidade: boolean,
): { setor: string; id: string } | null {
  const todos = grupos.flatMap((g) => g.itens.map((i) => ({ setor: g.setor, i })));
  const idx = todos.findIndex((x) => x.i.id === idAtual);
  const atual = todos[idx];
  if (!atual) {
    return null;
  }
  const prox = ehUnidade
    ? todos.slice(idx + 1).find((x) => x.setor === atual.setor && x.i.status !== "concluido")
    : [...todos.slice(idx + 1), ...todos.slice(0, idx)].find((x) => x.i.status === "em_andamento");
  return prox ? { setor: prox.setor, id: prox.i.id } : null;
}

/** Grupo de um setor (05A..05R), recolhível, com contador de concluídos. */
function GrupoSetor({
  grupo,
  feitos,
  total,
  aberto,
  onAlternar,
  abertoItemId,
  onAbrirItem,
  onAvancar,
  token,
  onSalvo,
  onNotificar,
  fatoEditavel,
  mostrarCodigo,
  mostrarContextoAvaliacao,
}: {
  grupo: Grupo;
  feitos: number;
  total: number;
  aberto: boolean;
  onAlternar: () => void;
  abertoItemId: string | null;
  onAbrirItem: (id: string, aberto: boolean) => void;
  onAvancar: (id: string) => void;
  token: string;
  onSalvo: (i: PlanoItem) => void;
  onNotificar?: (msg: string) => void;
  fatoEditavel?: boolean;
  mostrarCodigo?: boolean;
  mostrarContextoAvaliacao?: boolean;
}) {
  const tudoFeito = feitos === total;
  return (
    <div className={`pl-setor${tudoFeito ? " feito" : ""}`}>
      <button type="button" className="pl-setor-head" onClick={onAlternar} aria-expanded={aberto}>
        <span className="pl-chevron" aria-hidden="true">
          {aberto ? "▾" : "▸"}
        </span>
        <span className="pl-setor-nome">{grupo.nome}</span>
        <span className={`pl-setor-cont${tudoFeito ? " ok" : ""}`}>
          {feitos} de {total} concluídos
        </span>
      </button>
      {aberto && (
        <div className="pl-setor-itens">
          {grupo.itens.map((i) => (
            <ItemPlanoCard
              key={i.id}
              item={i}
              token={token}
              onSalvo={onSalvo}
              onNotificar={onNotificar}
              fatoEditavel={fatoEditavel}
              mostrarCodigo={mostrarCodigo}
              mostrarContextoAvaliacao={mostrarContextoAvaliacao}
              aberto={i.id === abertoItemId}
              onAberto={(v) => onAbrirItem(i.id, v)}
              onAvancar={() => onAvancar(i.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export interface SecoesPorSetorProps {
  itens: readonly PlanoItem[];
  token: string;
  onSalvo: (i: PlanoItem) => void;
  /** Aviso curto ("Plano salvo ✓") para a casca exibir (toast). */
  onNotificar?: (msg: string) => void;
  fatoEditavel?: boolean;
  mostrarCodigo?: boolean;
  mostrarContextoAvaliacao?: boolean;
  /**
   * Itens que contam no "X de Y concluídos" de cada setor (padrão: `itens`). O
   * avaliador passa o plano inteiro quando `itens` vem filtrado por situação —
   * senão "A revisar" mostraria "0 de 2 concluídos" num setor de 7.
   */
  universo?: readonly PlanoItem[];
}

/**
 * Renderiza os itens agrupados em setores recolhíveis — igual para a unidade e
 * para o avaliador. Um item aberto por vez em cada setor (unidade) ou no plano
 * (avaliador), com auto-avanço ao salvar/decidir.
 */
export function SecoesPorSetor({
  itens,
  token,
  onSalvo,
  onNotificar,
  fatoEditavel,
  mostrarCodigo,
  mostrarContextoAvaliacao,
  universo,
}: SecoesPorSetorProps) {
  const grupos = agruparPorSetor(itens);
  const ehUnidade = !fatoEditavel;
  const [inicio] = useState(() => aberturaInicial(grupos, ehUnidade));
  const [abertoPorSetor, setAbertoPorSetor] = useState<Record<string, string | null>>(inicio);
  const [setoresAbertos, setSetoresAbertos] = useState<ReadonlySet<string>>(() => {
    // Poucos setores → todos abertos; muitos (catálogo completo) → só o primeiro
    // (unidade) ou o do item que abriu (avaliador).
    if (grupos.length <= 4) {
      return new Set(grupos.map((g) => g.setor));
    }
    const primeiro = ehUnidade ? grupos[0]?.setor : (Object.keys(inicio)[0] ?? grupos[0]?.setor);
    return new Set(primeiro ? [primeiro] : []);
  });
  // Item que acabou de ser aberto pelo auto-avanço: rola até ele depois do render.
  const [foco, setFoco] = useState<{ id: string } | null>(null);
  useEffect(() => {
    if (foco) {
      document.getElementById(idDoCard(foco.id))?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [foco]);

  const contagem = new Map<string, { feitos: number; total: number }>();
  for (const it of universo ?? itens) {
    const c = contagem.get(setorDoItem(it)) ?? { feitos: 0, total: 0 };
    c.total += 1;
    c.feitos += it.status === "concluido" ? 1 : 0;
    contagem.set(setorDoItem(it), c);
  }

  const abrirItem = (setor: string, id: string, v: boolean) =>
    setAbertoPorSetor((prev) => (ehUnidade ? { ...prev, [setor]: v ? id : null } : v ? { [setor]: id } : {}));
  const alternarSetor = (setor: string) =>
    setSetoresAbertos((prev) => {
      const prox = new Set(prev);
      if (prox.has(setor)) {
        prox.delete(setor);
      } else {
        prox.add(setor);
      }
      return prox;
    });
  const avancar = (setorAtual: string, idAtual: string) => {
    const prox = proximoItem(grupos, idAtual, ehUnidade);
    setAbertoPorSetor((prev) => {
      if (ehUnidade) {
        return { ...prev, [setorAtual]: prox?.id ?? null }; // o próximo, ou recolhe
      }
      return prox ? { [prox.setor]: prox.id } : {};
    });
    if (prox) {
      setSetoresAbertos((prev) => new Set(prev).add(prox.setor));
      setFoco({ id: prox.id });
    }
  };

  return (
    <>
      {grupos.map((grupo) => {
        const c = contagem.get(grupo.setor) ?? { feitos: 0, total: grupo.itens.length };
        return (
          <GrupoSetor
            key={grupo.setor}
            grupo={grupo}
            feitos={c.feitos}
            total={c.total}
            aberto={setoresAbertos.has(grupo.setor)}
            onAlternar={() => alternarSetor(grupo.setor)}
            abertoItemId={abertoPorSetor[grupo.setor] ?? null}
            onAbrirItem={(id, v) => abrirItem(grupo.setor, id, v)}
            onAvancar={(id) => avancar(grupo.setor, id)}
            token={token}
            onSalvo={onSalvo}
            onNotificar={onNotificar}
            fatoEditavel={fatoEditavel}
            mostrarCodigo={mostrarCodigo}
            mostrarContextoAvaliacao={mostrarContextoAvaliacao}
          />
        );
      })}
    </>
  );
}
